import React from 'react';
import ReactECharts from 'echarts-for-react';

const getStat = (statsData, group, key) => {
  const split = statsData?.stats?.find(s => s.group?.displayName === group)?.splits?.[0]?.stat;
  return split?.[key] ?? null;
};

const StatRow = ({ label, awayVal, homeVal }) => {
  const a = parseFloat(awayVal) || 0;
  const h = parseFloat(homeVal) || 0;
  const max = Math.max(a, h, 0.001);
  const awayWins = a > h;
  const homeWins = h > a;

  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-[rgba(101,141,113,0.2)] last:border-0">
      <div className="flex items-center gap-1.5 flex-1 justify-end">
        <span className={`text-[10px] font-black tabular-nums ${awayWins ? 'text-[#a9c9b1]' : 'text-white'}`}>{awayVal ?? '--'}</span>
        <div className="h-1.5 bg-[rgba(0,0,0,0.3)] rounded-full overflow-hidden w-16">
          <div className="h-full rounded-full" style={{ width: `${(a / max) * 100}%`, backgroundColor: '#a9c9b1' }} />
        </div>
      </div>
      <span className="text-[9px] uppercase tracking-widest text-[#658d71] font-bold w-14 text-center shrink-0">{label}</span>
      <div className="flex items-center gap-1.5 flex-1">
        <div className="h-1.5 bg-[rgba(0,0,0,0.3)] rounded-full overflow-hidden w-16">
          <div className="h-full rounded-full" style={{ width: `${(h / max) * 100}%`, backgroundColor: '#c4333e' }} />
        </div>
        <span className={`text-[10px] font-black tabular-nums ${homeWins ? 'text-[#c4333e]' : 'text-white'}`}>{homeVal ?? '--'}</span>
      </div>
    </div>
  );
};

const TeamSeasonComparison = ({ awayStats, homeStats, awayAbbr, homeAbbr }) => {
  const radarOption = {
    backgroundColor: 'transparent',
    radar: {
      indicator: [
        { name: 'AVG', max: 0.32 },
        { name: 'OPS', max: 0.9 },
        { name: 'HR/G', max: 2 },
        { name: 'K/9', max: 12 },
        { name: 'ERA', max: 6 },
        { name: 'WHIP', max: 1.8 },
      ],
      shape: 'polygon',
      axisName: { color: '#a9c9b1', fontSize: 9, fontWeight: 'bold' },
      splitLine: { lineStyle: { color: 'rgba(101,141,113,0.3)' } },
      splitArea: { areaStyle: { color: ['rgba(0,0,0,0.05)', 'rgba(0,0,0,0.1)'] } },
      axisLine: { lineStyle: { color: 'rgba(101,141,113,0.4)' } },
    },
    tooltip: { backgroundColor: '#1e3a29', borderColor: '#658d71', textStyle: { color: '#d8ceb3', fontSize: 10 } },
    series: [{
      type: 'radar',
      data: [
        {
          value: [
            parseFloat(getStat(awayStats, 'hitting', 'avg')) || 0,
            parseFloat(getStat(awayStats, 'hitting', 'ops')) || 0,
            parseFloat(getStat(awayStats, 'hitting', 'homeRuns')) / 162 || 0,
            parseFloat(getStat(awayStats, 'pitching', 'strikeOuts')) / Math.max(parseFloat(getStat(awayStats, 'pitching', 'inningsPitched')) / 9, 1) || 0,
            parseFloat(getStat(awayStats, 'pitching', 'era')) || 0,
            parseFloat(getStat(awayStats, 'pitching', 'whip')) || 0,
          ],
          name: awayAbbr,
          lineStyle: { color: '#a9c9b1', width: 2 },
          areaStyle: { color: 'rgba(169,201,177,0.15)' },
          itemStyle: { color: '#a9c9b1' },
        },
        {
          value: [
            parseFloat(getStat(homeStats, 'hitting', 'avg')) || 0,
            parseFloat(getStat(homeStats, 'hitting', 'ops')) || 0,
            parseFloat(getStat(homeStats, 'hitting', 'homeRuns')) / 162 || 0,
            parseFloat(getStat(homeStats, 'pitching', 'strikeOuts')) / Math.max(parseFloat(getStat(homeStats, 'pitching', 'inningsPitched')) / 9, 1) || 0,
            parseFloat(getStat(homeStats, 'pitching', 'era')) || 0,
            parseFloat(getStat(homeStats, 'pitching', 'whip')) || 0,
          ],
          name: homeAbbr,
          lineStyle: { color: '#c4333e', width: 2 },
          areaStyle: { color: 'rgba(196,51,62,0.15)' },
          itemStyle: { color: '#c4333e' },
        },
      ],
    }],
    legend: { bottom: 0, textStyle: { color: '#a9c9b1', fontSize: 9 }, itemWidth: 10, itemHeight: 8 },
  };

  const battingStats = [
    ['AVG', getStat(awayStats, 'hitting', 'avg'), getStat(homeStats, 'hitting', 'avg')],
    ['OBP', getStat(awayStats, 'hitting', 'obp'), getStat(homeStats, 'hitting', 'obp')],
    ['SLG', getStat(awayStats, 'hitting', 'slg'), getStat(homeStats, 'hitting', 'slg')],
    ['OPS', getStat(awayStats, 'hitting', 'ops'), getStat(homeStats, 'hitting', 'ops')],
    ['HR', getStat(awayStats, 'hitting', 'homeRuns'), getStat(homeStats, 'hitting', 'homeRuns')],
    ['R/G', getStat(awayStats, 'hitting', 'runs'), getStat(homeStats, 'hitting', 'runs')],
  ];

  const pitchingStats = [
    ['ERA', getStat(awayStats, 'pitching', 'era'), getStat(homeStats, 'pitching', 'era')],
    ['WHIP', getStat(awayStats, 'pitching', 'whip'), getStat(homeStats, 'pitching', 'whip')],
    ['K/9', getStat(awayStats, 'pitching', 'strikeOuts'), getStat(homeStats, 'pitching', 'strikeOuts')],
    ['BB', getStat(awayStats, 'pitching', 'baseOnBalls'), getStat(homeStats, 'pitching', 'baseOnBalls')],
    ['HR Allowed', getStat(awayStats, 'pitching', 'homeRuns'), getStat(homeStats, 'pitching', 'homeRuns')],
    ['Saves', getStat(awayStats, 'pitching', 'saves'), getStat(homeStats, 'pitching', 'saves')],
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
        <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
          <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Season Snapshot</span>
          <p className="text-[9px] text-[#658d71] font-bold uppercase tracking-widest mt-0.5">Radar comparison across key metrics</p>
        </div>
        <div className="p-2">
          <ReactECharts option={radarOption} style={{ height: '220px' }} />
        </div>
      </div>

      <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
        <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)] flex justify-between">
          <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Batting</span>
          <div className="flex gap-4 text-[9px] font-black uppercase tracking-widest">
            <span className="text-[#a9c9b1]">{awayAbbr}</span>
            <span className="text-[#c4333e]">{homeAbbr}</span>
          </div>
        </div>
        <div className="p-3">
          {battingStats.map(([label, a, h]) => <StatRow key={label} label={label} awayVal={a} homeVal={h} />)}
        </div>
      </div>

      <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
        <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)] flex justify-between">
          <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Pitching</span>
          <div className="flex gap-4 text-[9px] font-black uppercase tracking-widest">
            <span className="text-[#a9c9b1]">{awayAbbr}</span>
            <span className="text-[#c4333e]">{homeAbbr}</span>
          </div>
        </div>
        <div className="p-3">
          {pitchingStats.map(([label, a, h]) => <StatRow key={label} label={label} awayVal={a} homeVal={h} />)}
        </div>
      </div>
    </div>
  );
};

export default TeamSeasonComparison;